
import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  IconButton,
  Toolbar,
  Box,
  Tooltip,
  Avatar,
  AppBar,
  Typography,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import MenuIcon from "@material-ui/icons/Menu";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DashboardIcon from "@material-ui/icons/Dashboard";
import GetAppIcon from "@material-ui/icons/GetApp";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import logo from "../assets/logo.png";
import { logout } from "@carrier-sce-sharedpackages/auth-module";
import appConfig from "../appConfig";

// const settings = ["UPLOADS", "DASHBOARD", "DOWNLOADS", "AHRIUPLOADS"];
const settings = ["UPLOADS", "DASHBOARD", "DOWNLOADS"];
const settings_sales = ["Downloads"];

const useStyles = makeStyles(() => ({
  appbar: { background: "#0060b9" },

  toolbar: {
    minHeight: 90,
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    justifyContent: "space-between",
    "@media (max-width: 960px)": { minHeight: 80 },
    "@media (max-width: 600px)": { minHeight: 72 },
  },

  leftGroup: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    gap: 12,
    flex: "1 1 auto",
  },

  rightGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: "0 0 auto",
    position: "relative",
    zIndex: 2,
  },

  logoLink: {
    display: "block",
    lineHeight: 0,
  },

  avatar: {
    backgroundColor: "transparent",
    width: "100px",
    height: "100px",
    borderRadius: 8,
    "& .MuiAvatar-img": {
      objectFit: "contain",
      objectPosition: "center",
    },
    "@media (max-width: 960px)": {
      width: "72px",
      height: "72px",
    },
    "@media (max-width: 600px)": {
      width: "56px",
      height: "56px",
    },
  },

  titleBox: {
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
  },

  titleLink: {
    color: "inherit",
    textDecoration: "none",
  },

  // Responsive title sizes
  titleTypography: {
    fontSize: "1.25rem",
    "@media (min-width: 961px)": { fontSize: "1.35rem" },
    "@media (max-width: 960px)": { fontSize: "1.1rem" },
    "@media (max-width: 600px)": { fontSize: "1rem" },
  },
  subtitleTypography: {
    fontSize: "0.9rem",
    "@media (min-width: 961px)": { fontSize: "0.95rem" },
    "@media (max-width: 960px)": { fontSize: "0.85rem" },
    "@media (max-width: 600px)": { fontSize: "0.8rem" },
  },

  tabs: {
    "& .MuiTabs-indicator": { backgroundColor: "navy", height: 10, borderRadius: 6 },
    overflowX: "auto",
    overflowY: "hidden",
    whiteSpace: "nowrap",
    "@media (max-width: 960px)": { display: "none" }, // hide tabs on mobile
  },

  tab: {
    color: "white",
    opacity: 0.85,
    fontSize: "1.05rem",
    textTransform: "none",
    fontWeight: 500,
    mx: 2,
    "&.Mui-selected": {
      color: "white",
      fontWeight: "bold",
      backgroundColor: "rgba(0,0,128,0.25)",
      borderRadius: 6,
    },
  },

  mobileMenuButton: {
    display: "none",
    color: "white",
    "@media (max-width: 960px)": { display: "inline-flex" },
  },

  // Styled Menu (mobile)
  menuPaper: {
    minWidth: 260,
    borderRadius: 12,
    marginTop: 8,
    background:
      "linear-gradient(0deg, rgba(255,255,255,0.98), rgba(255,255,255,0.98))",
    boxShadow:
      "0 10px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)",
  },
  menuItem: {
    padding: "10px 14px",
    "&:hover": {
      backgroundColor: "rgba(0,96,185,0.08)",
    },
  },
  menuIcon: {
    color: "#0060b9",
    minWidth: 36,
  },
  menuTextPrimary: {
    fontWeight: 600,
    color: "#0d2a52",
    fontSize: "0.95rem",
  },
  menuTextSecondary: {
    color: "#5b6b7a",
    fontSize: "0.8rem",
  },
  menuChevron: {
    color: "#8aa0b6",
    marginLeft: 8,
  },
  menuSectionLabel: {
    padding: "8px 14px",
    color: "#5b6b7a",
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: 0.2,
  },
}));

function Header(props) {
  const classes = useStyles();
  const { username, flag } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const tabsList = flag ? settings_sales : settings;

  // Selected tab index based on the current URL
  const selectedTab = tabsList.findIndex(
    (tab) => `/${tab}`.toLowerCase() === location.pathname.toLowerCase()
  );

  const handleTabChange = (event, newValue) => {
    navigate(`/${tabsList[newValue]}`);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("user");
    logout(appConfig.api);
  };

  // Mobile menu state
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const isMenuOpen = Boolean(menuAnchor);
  const openMenu = (event) => setMenuAnchor(event.currentTarget);
  const closeMenu = () => setMenuAnchor(null);
  const go = (path) => {
    navigate(path);
    closeMenu();
  };

  // Map labels -> icons + captions (mobile only, desktop stays as Tabs)
  const menuItems = [
    {
      label: "Uploads",
      path: "/uploads",
      icon: <CloudUploadIcon />,
      caption: "Add or import new files",
    },
    {
      label: "Dashboard",
      path: "/dashboard",
      icon: <DashboardIcon />,
      caption: "Audit Upload Files",
    },
    {
      label: "Downloads",
      path: "/downloads",
      icon: <GetAppIcon />,
      caption: "Export processed datasets",
    }
    // {
    //   label: "AHRI Uploads",
    //   path: "/ahriuploads",
    //   icon: <AssignmentTurnedInIcon />,
    //   caption: "Submit AHRI data",
    // },
  ];

  // If sales flag is true, filter & normalize to match your settings_sales
  const filteredMenuItems = flag
    ? menuItems.filter((m) => m.label.toLowerCase() === "downloads")
    : menuItems;

  return (
    <AppBar position="static" className={classes.appbar}>
      <Container maxWidth="xl">
        <Toolbar variant="dense" className={classes.toolbar}>
          {/* Left: Logo + Title */}
          <Box className={classes.leftGroup}>
            <Link to="/" className={classes.logoLink}>
              <Avatar
                aria-label="Logo"
                alt="Company logo"
                src={logo}
                className={classes.avatar}
                variant="square"
              />
            </Link>

            <Box className={classes.titleBox}>
              <Typography
                variant="h6"
                noWrap
                className={classes.titleTypography}
                component="div"
              >
                <Link to="/" className={classes.titleLink}>
                  Ratings Agency Database
                </Link>
              </Typography>
              <Typography
                variant="body2"
                noWrap
                color="inherit"
                className={classes.subtitleTypography}
              >
                One Stop For Data
              </Typography>
            </Box>
          </Box>

          {/* Center: Tabs (desktop only) */}
          <Tabs
            value={selectedTab === -1 ? false : selectedTab}
            onChange={handleTabChange}
            textColor="inherit"
            className={classes.tabs}
          >
            {tabsList.map((tab) => (
              <Tab key={tab} label={tab} className={classes.tab} />
            ))}
          </Tabs>

          {/* Right: Menu (mobile) + Logout */}
          <Box className={classes.rightGroup}>
            {/* Mobile Menu Button */}
            <IconButton
              aria-label="Open navigation menu"
              className={classes.mobileMenuButton}
              onClick={openMenu}
            >
              <MenuIcon />
            </IconButton>

            {/* Mobile Menu (styled) */}
            <Menu
              id="header-nav-menu"
              anchorEl={menuAnchor}
              open={isMenuOpen}
              onClose={closeMenu}
              keepMounted
              // Paper props let us style the menu surface
              PaperProps={{ className: classes.menuPaper }}
              // Optional alignment tweak:
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box className={classes.menuSectionLabel}>NAVIGATION</Box>
              {filteredMenuItems.map((item, idx) => (
                <React.Fragment key={item.path}>
                  <MenuItem
                    className={classes.menuItem}
                    onClick={() => go(item.path)}
                    dense
                  >
                    <ListItemIcon className={classes.menuIcon}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      secondary={item.caption}
                      primaryTypographyProps={{
                        className: classes.menuTextPrimary,
                      }}
                      secondaryTypographyProps={{
                        className: classes.menuTextSecondary,
                      }}
                    />
                    <ChevronRightIcon className={classes.menuChevron} />
                  </MenuItem>
                  {idx === 0 || idx === filteredMenuItems.length - 1 ? (
                    <Divider />
                  ) : null}
                </React.Fragment>
              ))}

              {/* Optional: Quick actions section */}
              {/* <Box className={classes.menuSectionLabel}>QUICK ACTIONS</Box>
              <MenuItem className={classes.menuItem} onClick={() => go('/downloads')} dense>
                <ListItemIcon className={classes.menuIcon}>
                  <GetAppIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Latest exports"
                  secondary="See recent files"
                  primaryTypographyProps={{ className: classes.menuTextPrimary }}
                  secondaryTypographyProps={{ className: classes.menuTextSecondary }}
                />
                <ChevronRightIcon className={classes.menuChevron} />
              </MenuItem> */}
            </Menu>

            {/* Logout */}
            <Tooltip title={`Not ${username}? Logout`}>
              <IconButton onClick={handleLogout} style={{ color: "white" }}>
                <ExitToAppIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default Header;
